import {useFragment, useRelayEnvironment} from 'react-relay'
import {graphql} from 'relay-runtime'
import type {LabelRow$key} from './__generated__/LabelRow.graphql'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemActionBar} from '@github-ui/list-view/ListItemActionBar'
import {ActionList, ConfirmationDialog, Link, Tooltip} from '@primer/react'
import {LabelToken} from '@github-ui/label-token'
import {GitPullRequestIcon, IssueOpenedIcon} from '@primer/octicons-react'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import styles from './RepositoryLabel.module.css'
import {type SafeHTMLString, SafeHTMLText} from '@github-ui/safe-html'
import {LABELS} from './constants/labels'
import {useCallback, useState} from 'react'
import {commitDeleteLabelMutation} from './mutations/delete-label-mutation'
import {Banner} from '@primer/react/experimental'
import {DialogForm as EditDialog} from './DialogForm'
import {formatFormErrorMessage} from './utils'
import {commitUpdateLabelMutation} from './mutations/update-label-mutation'

type LabelRowProps = {
  label: LabelRow$key
  isActionsAvailable: boolean
  viewerCanPush: boolean
}

export function LabelRow({label, isActionsAvailable, viewerCanPush}: LabelRowProps) {
  const [errorsPresent, setErrorsPresent] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editSubmissionErrors, setEditSubmissionErrors] = useState('')

  const data = useFragment(
    graphql`
      fragment LabelRow on Label {
        id
        name
        nameHTML
        color
        description
        issues {
          totalCount
        }
        pullRequests {
          totalCount
        }
        repository {
          id
        }
      }
    `,
    label,
  )

  const openIssues = data.issues?.totalCount || 0
  const openIssuesQuery = `issues?q=${encodeURIComponent(`is:open label:"${data.name}"`)}`
  const openPullRequests = data.pullRequests?.totalCount || 0
  const openPullRequestsQuery = `issues?q=${encodeURIComponent(`is:open is:pr label:"${data.name}"`)}`

  const environment = useRelayEnvironment()
  const handleDelete = useCallback(async () => {
    commitDeleteLabelMutation({
      environment,
      input: {id: data.id},
      onCompleted: () => {
        setErrorsPresent(false)
        setIsDialogOpen(false)

        // Upate the label counter in the header
        environment.commitUpdate(store => {
          const connectionId = `client:${data.repository.id}:__LabelList_labels_connection(orderBy:{"direction":"ASC","field":"NAME"},skip:0)`
          const labelsConnection = store.get(connectionId)
          if (labelsConnection) {
            const totalCount = labelsConnection.getValue('totalCount') as number
            labelsConnection.setValue(Math.max(totalCount - 1, 0), 'totalCount')
          }
        })
      },
      onError: () => {
        setErrorsPresent(true)
      },
    })
  }, [data.id, data.repository.id, environment])

  const handleEdit = useCallback(
    async (
      input: {
        name: string
        description: string
        color: string
      },
      setIsSubmitting: (isSubmitting: boolean) => void,
    ) => {
      setEditSubmissionErrors('')

      commitUpdateLabelMutation({
        environment,
        input: {
          id: data.id,
          name: input.name,
          description: input.description,
          color: input.color,
        },
        onCompleted: response => {
          if (!response.updateLabel?.label) {
            setEditSubmissionErrors(LABELS.editLabelError)
            setIsSubmitting(false)
            return
          }
          setIsSubmitting(false)
          setIsEditDialogOpen(false)
        },
        onError: (error: Error) => {
          if (error.cause && Array.isArray(error.cause)) {
            setEditSubmissionErrors(formatFormErrorMessage(error.cause))
          } else {
            setEditSubmissionErrors(LABELS.editLabelError)
          }
          setIsSubmitting(false)
        },
      })
    },
    [setEditSubmissionErrors, environment, data],
  )

  return (
    <ListItem
      key={data.id}
      title={<></>} // We need to solve this. title is important for announcing the list item but we don't have a title concept here.
      role="listitem"
      secondaryActions={
        isActionsAvailable ? (
          <ListItemActionBar
            staticMenuActions={[
              ...(viewerCanPush
                ? [
                    {
                      key: 'edit',
                      render: () => <ActionList.Item onSelect={() => setIsEditDialogOpen(true)}>Edit</ActionList.Item>,
                    },
                  ]
                : []),
              ...(viewerCanPush
                ? [
                    {
                      key: 'delete',
                      render: () => (
                        <>
                          <ActionList.Divider />
                          <ActionList.Item variant="danger" onSelect={() => setIsDialogOpen(true)}>
                            {LABELS.deleteButtonText}
                          </ActionList.Item>
                        </>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        ) : undefined
      }
    >
      <ListItemMainContent>
        <ListItemDescription className={styles.labelRowDescription}>
          <ListItemDescriptionItem>
            <Link href={`labels/${encodeURIComponent(data.name)}`} muted>
              <LabelToken
                text={<SafeHTMLText html={data.nameHTML as SafeHTMLString} />}
                key={0}
                fillColor={`#${data.color}`}
                // LabelToken does not support className yet.
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  cursor: 'pointer',
                  maxWidth: '100%',
                }}
              />
            </Link>{' '}
          </ListItemDescriptionItem>
          <ListItemDescriptionItem className={styles.labelRowDescriptionItemDescription}>
            {data.description}
          </ListItemDescriptionItem>
          <ListItemDescriptionItem className={styles.labelRowIssuesAndPrsCount}>
            {openIssues > 0 ? (
              <Tooltip text={`${openIssues} open issues`} direction="n">
                <Link href={openIssuesQuery} muted>
                  <IssueOpenedIcon size={16} /> {openIssues}
                </Link>
              </Tooltip>
            ) : null}
            {openPullRequests > 0 ? (
              <Tooltip text={`${openPullRequests} open pull requests`} direction="n">
                <Link href={openPullRequestsQuery} muted>
                  <GitPullRequestIcon size={16} /> {openPullRequests}
                </Link>
              </Tooltip>
            ) : null}
          </ListItemDescriptionItem>
        </ListItemDescription>
      </ListItemMainContent>
      {isDialogOpen && (
        <ConfirmationDialog
          onClose={gesture => {
            if (gesture === 'confirm') {
              handleDelete()
            } else {
              setErrorsPresent(false)
              setIsDialogOpen(false)
            }
          }}
          title={<p>{LABELS.labelDeleteDialogTitle}</p>}
          cancelButtonContent={LABELS.cancelButtonText}
          confirmButtonContent={LABELS.deleteLabelButtonText}
          confirmButtonType="danger"
        >
          <div>
            {errorsPresent && (
              <Banner
                className="mb-3"
                aria-label="Error deleting label"
                aria-live="assertive"
                title="Error"
                description={LABELS.deleteLabelError}
                variant="critical"
              />
            )}
            <p>{LABELS.deleteLabelWarningPermanent}</p>
            <p>{LABELS.deleteLabelAssociatedIssuesNote}</p>
          </div>
        </ConfirmationDialog>
      )}
      {isEditDialogOpen && (
        <EditDialog
          onDialogClose={() => {
            setEditSubmissionErrors('')
            setIsEditDialogOpen(false)
          }}
          formTitle={LABELS.editLabel}
          submissionErrors={editSubmissionErrors}
          submitButtonText={LABELS.saveChanges}
          onDialogSubmit={handleEdit}
          initialValues={{
            name: data.name,
            description: data.description,
            color: data.color,
          }}
        />
      )}
    </ListItem>
  )
}

try{ LabelRow.displayName ||= 'LabelRow' } catch {}